import { useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useTheme } from '@material-ui/core';

import { Theme, Tooltip } from '@bizzabo/design-system';
import { PopperPlacementType } from '@material-ui/core/Popper';

import useIsMobile from '../../../hooks/useIsMobile';
import { AppToolId, IAwareness } from '../../types/appTools';
import useAwarenessNotification, { AwarenessType } from '../../hooks/useAwarenessNotification';
import AppToolBarTooltip from './AppToolBarTootltip';

interface AppToolbarItemAwarenessProps {
    awareness: IAwareness;
    toolId: AppToolId;
    onAppSelectedHandle: (toolId: AppToolId) => void;
    children: ({ isDotAwarenessOn, anchorRef, awarenessHandle }) => JSX.Element;
}

const AppToolbarItemAwareness: React.FC<AppToolbarItemAwarenessProps> = ({
    awareness,
    toolId,
    onAppSelectedHandle,
    children,
}: AppToolbarItemAwarenessProps) => {
    const classes = useStyles();
    const theme = useTheme<Theme>();
    const isMobile = useIsMobile();
    const anchorRef = useRef<HTMLDivElement>(null);
    const { awarenessType, closeTooltipAwareness, awarenessHandle } = useAwarenessNotification({
        awareness,
        toolId,
    });

    const isDotAwarenessOn =
        awarenessType === AwarenessType.DOT || awarenessType === AwarenessType.ALL;
    const isTooltipAwarenessOn =
        awarenessType === AwarenessType.TOOLTIP || awarenessType === AwarenessType.ALL;

    const onAppSelected = (toolId: AppToolId) => {
        onAppSelectedHandle(toolId);
        awarenessHandle();
    };

    return (
        <Tooltip
            customBackgroundColor={theme.customization.interactive.G10}
            open={isTooltipAwarenessOn}
            setOpen={closeTooltipAwareness}
            placement={`${isMobile ? 'top' : 'left-start'}` as PopperPlacementType}
            anchorRef={anchorRef}
            text={
                awareness?.tooltip?.customComponent ? (
                    <awareness.tooltip.customComponent
                        onClose={closeTooltipAwareness}
                        onOpenApp={() => onAppSelected(toolId)}
                    />
                ) : (
                    <AppToolBarTooltip
                        icon={awareness?.tooltip?.icon}
                        title={awareness?.tooltip?.title}
                        text={awareness?.tooltip?.text}
                        onClose={closeTooltipAwareness}
                        onOpenApp={() => onAppSelected(toolId)}
                    />
                )
            }
            paperClassName={classes.tooltipPaper}
            boundariesElement="viewport"
            disableSlideAnimation={false}
            clickAwayDisabled={true}
        >
            {children({ isDotAwarenessOn, anchorRef, awarenessHandle })}
        </Tooltip>
    );
};

const useStyles = makeStyles(
    {
        tooltipPaper: {
            boxShadow: '0px 4px 14px rgba(0, 0, 0, 0.1)',
        },
    },
    {
        classNamePrefix: 'AppToolbarItemAwareness',
    }
);

export default AppToolbarItemAwareness;
